import React, { useCallback, useRef } from "react";

import { useTranslation } from "react-i18next";

import { useDimensions } from "@app/hooks/useDimensions";

import { Stack } from "@fermions";

import { FontSize, FontWeight, Text } from "@atoms/Text";

import "./StepsProgressBar.scss";

interface StepsProgressBarProps {
  steps: {
    title: string;
    stepNumber: number;
  }[];
  completedStepNumber: number;
}

const StepsProgressBar = (props: StepsProgressBarProps) => {
  const { steps, completedStepNumber } = props;
  const { t } = useTranslation();
  const stepRef = useRef<HTMLDivElement>(null);

  const { width: stepWidth } = useDimensions(stepRef);

  const getStepProgressWidth = useCallback(
    (stepNumber: number) => {
      if (stepNumber <= completedStepNumber) {
        return "100%";
      }
      return "0%";
    },
    [completedStepNumber]
  );

  return (
    <div
      className="steps-progress-bar"
      style={{ marginRight: `-${Math.max(0, stepWidth - 35)}px` }}
    >
      <div className="steps-progress-bar__steps">
        {steps.map((step, index) => (
          <div
            ref={index === 0 ? stepRef : null}
            key={step.stepNumber}
            className={`steps-progress-bar__step`}
          >
            {index < steps.length - 1 ? (
              <div className="steps-progress-bar__bar">
                <div
                  className={`steps-progress-bar__bar--fill steps-progress-bar__bar--fill-complete`}
                  style={{ width: getStepProgressWidth(step.stepNumber) }}
                />
              </div>
            ) : null}
            <Stack>
              <div
                className={`steps-progress-bar__step__icon ${
                  index <= completedStepNumber
                    ? "steps-progress-bar__step__icon--completed"
                    : "steps-progress-bar__step__icon--non-complete"
                }
            `}
              ></div>
              <div
                className={`steps-progress-bar__step__title  ${
                  index <= completedStepNumber
                    ? "steps-progress-bar__step__title--completed"
                    : ""
                }`}
              >
                <Text
                  weight={FontWeight.BOLD}
                  size={FontSize.L}
                  text={t(step.title)}
                  truncate
                  maxWidth={stepWidth}
                />
              </div>
            </Stack>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StepsProgressBar;
